import React, {Fragment, useCallback} from "react";
import {IconButton} from "@mui/material";
import {get, partial} from "lodash";
import {ShareType, Language} from "modules/enums";
import {QuestionLogo} from "components/Questions/Logo";
import {ReactComponent as ShareIcon} from "assets/images/icons/share.svg";
import {ReactComponent as FacebookIcon} from "assets/images/icons/facebook.svg";
import {ReactComponent as XIcon} from "assets/images/icons/x.svg";
import styled from "styled-components";
import {useIsMobile, useStore} from "modules/hooks";
import {LOCALE, SHARE_DISABLED} from "modules/constants";
import {createConnextraScriptTag, share, ConnextraType} from "modules/utils";
import {Exist} from "components/Exist";

const FinalTitle = styled.h3`
	font-size: 34px;
	max-width: 400px;
	line-height: 34px;

	margin-top: 40px;
	@media (max-width: 840px) {
		max-width: none;
		text-align: center;
		margin-top: 96px;
		font-size: 24px;
		line-height: 24px;
	}
`;

const SocialButtons = styled.span`
	display: inline-block;
	white-space: nowrap;

	@media (max-width: 840px) {
		margin-top: 45px;
	}
`;

const ResetLink = styled.a`
	text-decoration: underline;
	cursor: pointer;

	&:hover {
		text-decoration: none;
	}
`;

const FinalDescription = styled.div`
	margin-top: 45px;
	font-weight: 700;
	display: flex;
	flex-wrap: wrap;
	line-height: 34px;
	align-items: flex-end;

	br {
		display: none;
	}

	@media (max-width: 840px) {
		flex-direction: column;
		justify-content: center;
		align-items: center;
		text-align: center;
		margin-top: 20px;
		line-height: 22px;

		${ResetLink}, .doesnt_feel_right {
			display: none;
		}

		br {
			display: inline;
		}
	}
`;

const IconButtonStyled = styled(IconButton)`
	&& {
		padding-bottom: 6px;
	}

	&:first-child {
		padding-right: 4px;
	}

	&:last-child {
		padding-left: 4px;
	}
`;
const shareMessage = {
	[Language.English]: (name: string) => {
		return `I'm the newest fan of the ${name}! Need a CFL team? Play the Team Picker to find your best match.`;
	},
	[Language.French]: (name: string, id?: number) => {
		if (id === 3) {
			return `Je suis un nouveau partisan du ROUGE et NOIR d'Ottawa! Vous cherchez une équipe de la LCF à suivre? Jouez au Sélecteur d’équipe pour découvrir l’équipe parfaite pour vous.`;
		}

		return `Je suis un nouveau partisan des ${name}! Vous cherchez une équipe de la LCF à suivre? Jouez au Sélecteur d’équipe pour découvrir l’équipe parfaite pour vous.`;
	},
}[LOCALE];

export const FinalPageDescription: React.FC = () => {
	const {I18n, UIQuestionsProgress, UISquadList} = useStore();
	const {isMobileShare} = useIsMobile("(max-width: 840px)");
	const topSquad = UISquadList.topSquad;
	const name = get(topSquad?.fullName, LOCALE, "");
	const translateArgs = {X: name};

	const resetProgressHandler = useCallback(() => {
		UIQuestionsProgress.resetProgress();
	}, [UIQuestionsProgress]);

	const shareHandler = useCallback(
		(type: ShareType) => {
			const message = I18n.has("social.message")
				? I18n.t("social.message", {name})
				: shareMessage(name, topSquad?.id);

			share({sc: type, squadId: topSquad?.id, message});

			createConnextraScriptTag(ConnextraType.SHARE_CLICK);
		},
		[topSquad, name, I18n]
	);

	return (
		<Fragment>
			<QuestionLogo />

			<Exist when={!SHARE_DISABLED}>
				<FinalTitle>{I18n.t("screens.final.title", translateArgs)}</FinalTitle>
			</Exist>

			<Exist when={SHARE_DISABLED}>
				<FinalTitle>{I18n.t("screens.final_no_share.title", translateArgs)}</FinalTitle>
			</Exist>

			<FinalDescription>
				<Exist when={!SHARE_DISABLED}>
					{`${I18n.t("screens.final.share_i_love")} `}
					<br />
					{I18n.t("screens.final.share_tell")}
					<SocialButtons>
						{isMobileShare ? (
							<IconButton onClick={partial(shareHandler, ShareType.MOBILE)}>
								<ShareIcon />
							</IconButton>
						) : (
							<Fragment>
								<IconButtonStyled onClick={partial(shareHandler, ShareType.FB)}>
									<FacebookIcon />
								</IconButtonStyled>
								<IconButtonStyled onClick={partial(shareHandler, ShareType.TW)}>
									<XIcon />
								</IconButtonStyled>
							</Fragment>
						)}
					</SocialButtons>
				</Exist>

				<span className="doesnt_feel_right">
					{I18n.t("screens.final.share.doesnt_feel_right")}{" "}
				</span>
				<ResetLink onClick={resetProgressHandler}>
					{I18n.t("screens.final.share.try_again")}
				</ResetLink>
			</FinalDescription>
		</Fragment>
	);
};
